/**
 * Widget Registry — single source of truth for all widget definitions.
 *
 * Import direction (no circular deps):
 *   types.ts → widget-types.ts → widget-definitions/* → this file
 *   section-types/index.ts imports from this file (derives SECTION_TYPES)
 */

import type { WidgetDefinition } from '../widget-types';
import type { SectionTypeDefinition } from '../types';

import { heroWidget } from './hero';
import { productGridWidget } from './product-grid';
import { categoryGridWidget } from './category-grid';
import { announcementBarWidget } from './announcement-bar';
import { textBlockWidget } from './text-block';
import { productCarouselWidget } from './product-carousel';
import { featuredProductsWidget } from './featured-products';
import { productsWeekWidget } from './products-week';
import { staticCategoriesWidget } from './static-categories';
import { containerFrameWidget } from './container-frame';
import { blockWidget } from './block';
import { imageWidget } from './image';
import { exogroHeaderWidget } from './code/exogro-header';
import { exogroFooterWidget } from './code/exogro-footer';
import { exogroProductCardWidget } from './code/exogro-product-card';
import { loyaltyShellWidget } from './code/loyalty-shell';
import { imageSliderWidget } from './image-slider';

/**
 * All registered widget definitions.
 * 15 entries: 13 unique types + `categories` alias (same object as `category-grid`).
 */
export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  'hero': heroWidget,
  'product-grid': productGridWidget,
  'category-grid': categoryGridWidget,
  'categories': categoryGridWidget, // Alias — same object reference
  'announcement-bar': announcementBarWidget,
  'text-block': textBlockWidget,
  'product-carousel': productCarouselWidget,
  'featured-products': featuredProductsWidget,
  'products-week': productsWeekWidget,
  'static-categories': staticCategoriesWidget,
  'container-frame': containerFrameWidget,
  block: blockWidget,
  'image': imageWidget,
  'code:exogro-header': exogroHeaderWidget,
  'code:exogro-footer': exogroFooterWidget,
  'code:exogro-product-card': exogroProductCardWidget,
  'code:loyalty-shell': loyaltyShellWidget,
  'image-slider': imageSliderWidget,
};

/**
 * Derive SECTION_TYPES from WIDGET_REGISTRY for backward compatibility.
 * Returns the `schema` field of each widget as a SectionTypeDefinition.
 */
export function toSectionTypes(
  registry: Record<string, WidgetDefinition>
): Record<string, SectionTypeDefinition> {
  return Object.fromEntries(
    Object.entries(registry).map(([key, w]) => [key, w.schema])
  );
}

// Re-export individual widgets for direct access
export { heroWidget } from './hero';
export { productGridWidget } from './product-grid';
export { categoryGridWidget } from './category-grid';
export { announcementBarWidget } from './announcement-bar';
export { textBlockWidget } from './text-block';
export { productCarouselWidget } from './product-carousel';
export { featuredProductsWidget } from './featured-products';
export { productsWeekWidget } from './products-week';
export { staticCategoriesWidget } from './static-categories';
export { containerFrameWidget } from './container-frame';
export { blockWidget } from './block';
export { imageWidget } from './image';
export { exogroHeaderWidget } from './code/exogro-header';
export { exogroFooterWidget } from './code/exogro-footer';
export { exogroProductCardWidget } from './code/exogro-product-card';
